<template>
  <page
    :name="$t('Fatture e pagamenti')"
    :empty="invoices.length <= 0"
    :state="invoicesState"
  >
    <payments-list :invoice-id="0" pending-only :title="$t('Pagamenti')" />

    <pf-page-section>
      <pf-title h="3">{{ $t('Le tue fatture') }}</pf-title>

      <pf-data-list expandable>
        <pf-data-list-item
          v-for="row in invoices"
          :key="row.id"
          :expanded="row.expanded"
          @update:expanded="row.expanded = !row.expanded"
        >
          <pf-data-list-cell style="max-width: 25px;">
            <pf-tooltip>
              <template #content>{{ tooltip(row) }}</template>
              <pf-icon v-if="row.type == 'Nota credito'" size="lg" status="success">
                <receipt-icon />
              </pf-icon>
              <pf-icon v-else-if="(row.payments - row.settled > 0 && row.pastdue == 0)" size="lg" status="warning">
                <credit-card-icon />
              </pf-icon>
              <pf-icon v-else-if="row.payments - row.settled <= 0" size="lg" status="success">
                <ok-icon />
              </pf-icon>
              <pf-icon v-else size="lg" status="danger">
                <warning-triangle-icon />
              </pf-icon>
            </pf-tooltip>
          </pf-data-list-cell>

          <pf-data-list-cell>
            <pf-content>
              <h4>{{ row.type }} {{ row.nr }}</h4>
              <small>{{ $t('del {0}', [$date(row.date)]) }}</small>
            </pf-content>
          </pf-data-list-cell>

          <pf-data-list-cell>
            <pf-content>
              <h4>{{ $t('Importo') }}</h4>
              <p>{{ $currency(row.amount, row.currency) }}</p>
            </pf-content>
          </pf-data-list-cell>

          <pf-data-list-cell v-if="row.type != 'Nota credito'">
            <pf-content>
              <h4>{{ $t('Rate saldate') }}</h4>
              <p>{{ row.settled }} / {{ row.payments }}</p>
            </pf-content>
          </pf-data-list-cell>

          <pf-data-list-action class="pf-v6-u-flex-direction-column pf-v6-u-flex-direction-row-on-md">
            <pf-button variant="secondary" small target="_blank" :href="`/rest/invoice-pdf/${row.id}`" :title="$t('Scarica PDF')">
              <template #icon><file-pdf-icon /></template>
              <span class="pf-v6-u-display-none pf-v6-u-display-inline-on-sm">{{ $t('Download') }}</span>
            </pf-button>
            <pf-button
              v-if="row.type !== 'Nota credito' && row.payments - row.settled > 0" variant="primary" small @click="expand(row.id)">
              <template #icon><credit-card-icon /></template>
              <span class="pf-v6-u-display-none pf-v6-u-display-inline-on-sm">{{ $t('Paga ora') }}</span>
            </pf-button>
          </pf-data-list-action>

          <pf-data-list-content>
            <invoice-detail v-if="row" :invoice-id="row.id" />
            <payments-list
              v-if="row && row.type != 'Nota credito'"
              :invoice-id="row.id"
              :paymethod="row.paymethod"
              :currency="row.currency"
            />
          </pf-data-list-content>
        </pf-data-list-item>
      </pf-data-list>
    </pf-page-section>
  </page>
</template>

<script lang="ts" setup>
import { asyncComputedList } from '@common/asyncComputed';
import PaymentsList from '../components/payments-list.vue';
import InvoiceDetail from '../components/invoice-detail.vue';
import { Resource, type GenericResource } from '@/resources';
import { $t as _t } from '@/i18n';
import OkIcon from '@vue-patternfly/icons/ok-icon';
import WarningTriangleIcon from '@vue-patternfly/icons/warning-triangle-icon';
import FilePdfIcon from '@vue-patternfly/icons/file-pdf-icon';
import CreditCardIcon from '@vue-patternfly/icons/credit-card-icon';
import ReceiptIcon from '@vue-patternfly/icons/receipt-icon';

const { state: invoicesState, result: invoices } = asyncComputedList(async() => {
  const invoices = await new Resource('invoices').get();
  return invoices
    .map(i => {
      i.expanded = false;
      return i;
    });
});

function tooltip(invoice: GenericResource) {
  if (invoice.type == 'Nota credito') {
    return _t('Nota di credito');
  }
  if (invoice.payments - invoice.settled > 0 && invoice.pastdue == 0) {
    return _t('Fattura da saldare');
  }
  return invoice.payments - invoice.settled <= 0
    ? _t('Fattura saldata')
    : _t('Contiene rate non saldate e scadute');
}

function expand(id: number) {
  const invoice = invoices.value.find((f) => f.id === id);

  if (invoice) {
    invoice.expanded = !invoice.expanded;
  }
}
</script>
